import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { receiptPrinter } from '@shripos/shared/escpos';
import { useToast } from '@shripos/ui';
import { useSession } from '../../stores/session.js';
import { cachedGet } from '../offline/cache.js';
import { reconnectDevicePrinter } from './devices.js';
import * as svc from './service.jsx';

/** The current outlet's printers (live: socket 'printers:updated' / 'printers:status'). */
export function usePrinters() {
  const outletId = useSession((s) => s.me?.outlet?.id);
  return useQuery({
    queryKey: ['printers', outletId],
    queryFn: () => cachedGet('/printers', `printers:${outletId}`).then((r) => r.items),
    enabled: Boolean(outletId),
    networkMode: 'always',
    staleTime: 30_000,
  });
}

export function useReceiptPrinter() {
  const q = usePrinters();
  return receiptPrinter(q.data ?? []);
}

/**
 * Print actions with user feedback: errors become toasts with "Print in browser" (same
 * document through the print dialog) or "Reconnect" (USB/Bluetooth permission) actions.
 */
export function usePrinting() {
  const toast = useToast();
  return useMemo(() => {
    const run = async (fn, { success } = {}) => {
      try {
        const res = await fn();
        if (success && res && !res.skipped && res.via !== 'browser') toast.success(success);
        return res;
      } catch (err) {
        if (err?.cancelled) return null;
        if (err instanceof svc.PrintError) {
          if (err.pending) toast.info(err.message);
          else
            toast.error(err.message, {
              action: err.reconnect
                ? {
                    label: 'Reconnect printer',
                    onClick: () =>
                      reconnectDevicePrinter(err.printer)
                        .then(() => toast.success('Printer connected. Print again.'))
                        .catch((e) => toast.error(e)),
                  }
                : err.fallback
                  ? { label: 'Print in browser', onClick: () => err.fallback() }
                  : undefined,
            });
          return null;
        }
        toast.error(err);
        return null;
      }
    };
    return {
      receipt: (billId, opts) => run(() => svc.printReceipt(billId, opts)),
      kots: (kots, opts) => run(() => svc.printKots(kots, opts)),
      tokenSlip: (bill, opts) => run(() => svc.printTokenSlip(bill, opts)),
      creditNote: (billId, cnId) => run(() => svc.printCreditNote(billId, cnId)),
      test: (printer) => run(() => svc.testPrint(printer), { success: `Test page sent to ${printer.name}` }),
      drawer: (printer) => run(() => svc.openDrawer(printer), { success: 'Drawer opened' }),
      report: (build, opts) =>
        run(() => svc.printReport(build, opts), { success: `${opts?.label ?? 'Report'} printed` }),
    };
  }, [toast]);
}
